'use client';

import { useEffect, useState } from 'react';

import {
  formatFullDate,
  formatPublishedDate,
  formatRecency,
  formatTimeAgo,
} from '@repo/utils';

import { subscribeToMinuteTick } from './minute-tick';

/**
 * Renders a card timestamp without putting the clock into the cached HTML.
 *
 * First render (server, and the client's hydration pass) returns the absolute
 * date, which is a pure function of `iso`. The relative form arrives in an
 * effect, so it never reaches the ISR entry and never has to match across a
 * hydration boundary.
 *
 * It then keeps up with the clock. Holding the label in state is what makes
 * the first render deterministic, but it also means ordinary re-renders no
 * longer recompute it — so without the tick a card mounted as "just now"
 * would still claim that an hour later (caught in review on PR #401). The
 * subscription is to a single shared interval, not one timer per card, and it
 * is torn down on unmount.
 */
export const useRecencyLabel = (
  iso: string | null | undefined,
  /** `compact` ("3d ago", the feed cards) or `long` ("3 days ago", the video
   *  analysis surfaces, where the age sits on its own and is spelled out). */
  style: 'compact' | 'long' = 'compact',
): string => {
  const [absolute, relative] =
    style === 'long'
      ? [formatFullDate, formatTimeAgo]
      : [formatPublishedDate, formatRecency];
  const [label, setLabel] = useState(() => absolute(iso));

  useEffect(() => {
    // The relative form is '' for a missing or future date; keep the
    // absolute label rather than blanking the slot after mount.
    const refresh = () => setLabel(relative(iso) || absolute(iso));

    refresh();
    return subscribeToMinuteTick(refresh);
  }, [iso, absolute, relative]);

  return label;
};
